import {
  Avatar,
  Box,
  List,
  Tag,
  Text,
  WizardStep,
  WizardStepRenderParams,
} from '@factorialco/deprecated-design-system'
import DOMPurify from 'dompurify'

import { useFeatureAvailability } from 'modules/core/hooks/useFeatureAvailability'
import i18n from 'modules/core/lib/i18n'

import { ItemTemplate } from 'modules/processes/domain'
import type { WizardData } from 'modules/processes/pages/Modals/ProcessFromTemplateWizardPage/ProcessFromTemplateWizard/types'

const PreviewStep = ({ data: { template } }: WizardStepRenderParams<WizardData>) => {
  return (
    <Box flexDirection="column" gap="s40" paddingBottom="s40" padding="s4">
      <Box gap="s8">
        <Box>
          <Text.Header2>
            {i18n.t('processes.process_from_template.steps.preview.step_name')}
          </Text.Header2>
        </Box>
        <Box>
          <Text.Normal>
            {i18n.t('processes.process_from_template.steps.preview.description')}
          </Text.Normal>
        </Box>
      </Box>
      <Box boxShadow="s200" borderRadius={{ all: 'abs008' }} gap="s40" padding="s24">
        {template &&
          template.steps?.map((step, stepIndex: number) => (
            <Box key={stepIndex} gap="s12">
              <Text.Header2>{step.name}</Text.Header2>
              {step.items.map((item, itemIndex) => (
                <TemplateItem key={itemIndex} item={item} />
              ))}
            </Box>
          ))}
      </Box>
    </Box>
  )
}

const TemplateItem = ({ item }: { item: ItemTemplate }) => {
  const { featureAvailability, loading } = useFeatureAvailability(item?.feature)
  const showUpgrade = !featureAvailability.available && item?.feature && !loading

  const actionTitles = [
    {
      key: 'processes_send_custom_email_action',
      text: i18n.t('processes.process_from_template.actions.custom_email'),
    },
    {
      key: 'send_survey_action',
      text: i18n.t('processes.process_from_template.actions.survey'),
    },
  ]

  const actonTitle = actionTitles.find(
    (action) => action.key === item.action_configuration_identifier
  )

  const borderColor = showUpgrade ? 'champagne600' : 'grey400'

  const title =
    item.configuration_type === 'action' && actonTitle
      ? `${actonTitle.text}${item.title}`
      : item.title

  return (
    <Box
      flexDirection="row"
      flexGrow
      alignItems="center"
      justifyContent="spaceBetween"
      width="full"
      padding="s16"
      borderRadius={{ all: 'abs008' }}
      border={{ all: { color: borderColor, width: 's1', style: 'solid' } }}
    >
      <Box gap="s4" flexGrow>
        <Box gap="s4" flexDirection="row">
          <List.Title>{title}</List.Title>
          {showUpgrade && (
            <Tag inline type={Tag.Types.champagne}>
              {i18n.t('processes.process_from_template.upgrade')}
            </Tag>
          )}
        </Box>
        <List.Description>
          <div
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(item.instruction),
            }}
          />
        </List.Description>
      </Box>
      <Box justifyContent="center">
        <Avatar label="A B" size="s40" />
      </Box>
    </Box>
  )
}

export const previewStep = (): WizardStep<WizardData> => ({
  title: i18n.t('processes.process_from_template.steps.preview.step_name'),
  nextText: i18n.t('common.next'),
  render: (props) => <PreviewStep {...props} />,
  isCompleted: () => true,
})
